import { API_CONFIG } from 'config/api';

const AUTH_BASE_URL = API_CONFIG.AUTH.BASE_URL;

export interface LoginCredentials {
  email: string;
  password: string;
  // companySlug: string;
  rememberMe?: boolean;
}

export interface AuthResponse {
  success: boolean;
  data?: {
    user: {
      id: string;
      employerId: string;
      email: string;
      firstName: string;
      lastName: string;
      role: string;
      companyId: string;
      isActive: boolean;
    };
    company: {
      id: string;
      name: string;
      slug: string;
      domain: string;
      industry: string;
      address: string;
      phone: string;
      website: string;
    };
    tokens: {
      accessToken: string;
      refreshToken: string;
    };
  };
  message?: string;
}

export class AuthApiService {
  private getAuthHeaders() {
    const token = localStorage.getItem('vpd_auth_token') || sessionStorage.getItem('vpd_auth_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    };
  }

  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const response = await fetch(`${AUTH_BASE_URL}${API_CONFIG.AUTH.ENDPOINTS.LOGIN}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...credentials, role: 'EMPLOYER' }),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Login API error:', error);
      throw new Error('Failed to login');
    }
  }

  async refreshToken(): Promise<AuthResponse> {
    try {
      const refreshToken = localStorage.getItem('vpd_refresh_token') || sessionStorage.getItem('vpd_refresh_token');
      
      const response = await fetch(`${AUTH_BASE_URL}${API_CONFIG.AUTH.ENDPOINTS.REFRESH}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken }),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Refresh token API error:', error);
      throw new Error('Failed to refresh token');
    }
  }

  async logout(): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await fetch(`${AUTH_BASE_URL}${API_CONFIG.AUTH.ENDPOINTS.LOGOUT}`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Logout API error:', error);
      throw new Error('Failed to logout');
    }
  }

  async forgotPassword(email: string, companySlug: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await fetch(`${AUTH_BASE_URL}${API_CONFIG.AUTH.ENDPOINTS.FORGOT_PASSWORD}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, companySlug }),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Forgot password API error:', error);
      throw new Error('Failed to send password reset email');
    }
  }

  async resetPassword(token: string, newPassword: string, companySlug: string): Promise<{ success: boolean; message?: string }> {
    try {
      const response = await fetch(`${AUTH_BASE_URL}${API_CONFIG.AUTH.ENDPOINTS.RESET_PASSWORD}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, newPassword, companySlug }),
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Reset password API error:', error);
      throw new Error('Failed to reset password');
    }
  }

  async getCompanyPortal(slug: string): Promise<any> {
    try {
      const response = await fetch(`${AUTH_BASE_URL}/company-portal/${slug}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Get company portal API error:', error);
      throw new Error('Failed to fetch company portal info');
    }
  }
}


export const authApiService = new AuthApiService();
